/**
 * Portal Manta · AgenteIAPage
 */
import React, { useState, useEffect, useMemo, useCallback, Fragment, memo } from 'react';
import { V, S, sumBy, sigKey, pct, OBRAS_ROD } from '../../globals.js';
import { CONSOL_FIN } from '../../data/consol-fin.js';
import { PER_EXTRACTED } from '../../data/per-extracted.js';
import { executarPipeline } from '../agentes/executarPipeline.jsx';
import { AGENTES_CATALOG } from '../../data/agentes-catalog.js';
import { M } from '../../tokens/colors.js';

const AgenteIAPage = memo(function AgenteIAPage_Inner({lote}){
  const sig = sigKey(lote);
  const cf = CONSOL_FIN[sig]||{};
  const od = OBRAS_ROD[sig]||{};
  const ext = parseFloat((lote.extensao||'').replace(/[^\d,.]/g,'').replace(',','.'))||0;
  const totSicro = Object.values(cf).reduce((s,arr)=>s+sumBy(arr,'v'),0);
  const [estado,setEstado] = useState('idle'); // idle | rodando | concluido
  const [progresso,setProgresso] = useState([]);
  const [relatorio,setRelatorio] = useState(null);
  const [erroApi,setErroApi] = useState(null);

  // ── SUB-AGENTE 1 · Comparador PER × MEF ──
  function rodarComparador(){
    const perKey = 'L'+sig;
    const perData = PER_EXTRACTED[perKey];
    if(!perData){
      return {
        sub:'Comparador PER × MEF',
        status:'pendente',
        msg:`PER do Lote ${sig} ainda não foi processado pelo agente leitor. Resultado disponível só para L4 e L5.`,
        inconsistencias:[]
      };
    }
    const inc = [];
    // 1) Extensão lote
    const extPER = perData.lote?.extensao_km;
    const extDif = ext - (extPER||0);
    if(extPER && Math.abs(extDif)>1){
      inc.push({severidade:'media',campo:'extensao',valor_v9:ext,valor_per:extPER,diff:extDif});
    }
    // 2) Quantidade de rodovias
    const rodsV9 = (od.sistema||[]).length;
    const rodsPER = (perData.rodovias||[]).length;
    if(rodsV9 !== rodsPER){
      inc.push({severidade:'baixa',campo:'n_rodovias',valor_v9:rodsV9,valor_per:rodsPER,diff:rodsV9-rodsPER,
        nota:'Diferença pode ser por modelagem (contornos como rodovia separada vs englobada)'});
    }
    // 3) Σ extensão das rodovias
    const sumExtV9 = (od.sistema||[]).reduce((s,r)=>s+(r.ext||0),0);
    const sumExtPER = (perData.rodovias||[]).reduce((s,r)=>s+(r.ext_km||0),0);
    if(Math.abs(sumExtV9-sumExtPER)>5){
      inc.push({severidade:'media',campo:'soma_extensoes',valor_v9:sumExtV9,valor_per:sumExtPER,diff:sumExtV9-sumExtPER});
    }
    // 4) Para L4: detalhe duplicação
    if(sig==='4' && perData.rodovias_detalhe){
      const dupPER = perData.rodovias_detalhe.reduce((s,r)=>s+(r.duplicacao_km||0),0);
      const dupV9 = ((od.duplicacoes||[]).reduce((s,r)=>s+(r.km||0),0)) + ((od.contornos||[]).reduce((s,r)=>s+(r.km||0),0));
      if(Math.abs(dupV9-dupPER)>5){
        inc.push({severidade:'media',campo:'duplicacao_total_km',valor_v9:dupV9.toFixed(1),valor_per:dupPER.toFixed(1),diff:(dupV9-dupPER).toFixed(1)});
      }
    }
    return {
      sub:'Comparador PER × MEF',
      status: inc.length===0?'ok':(inc.some(i=>i.severidade==='alta')?'critico':'atencao'),
      msg:`${inc.length} inconsistências encontradas`,
      inconsistencias:inc,
      perKey,
      cobertura: perKey in PER_EXTRACTED ? 'L4/L5 disponíveis · L3 pendente de leitura' : 'pendente'
    };
  }

  // ── SUB-AGENTE 2 · Conclusão (template descritivo) ──
  function rodarConclusao(comp){
    const ratio = ((lote.capex_total_b||0)*1e9) ? totSicro/((lote.capex_total_b)*1e9) : 0;
    const rkm = ext ? totSicro/ext : 0;
    const benchmark_rkm_pr = 1340000; // média dos 3 lotes PR (R$ 1.34mi/km)
    const desvio_pct = benchmark_rkm_pr ? (rkm/benchmark_rkm_pr - 1)*100 : 0;
    // Disciplinas dominantes
    const disciplinas = Object.entries(cf).map(([d,items])=>({d,v:sumBy(items,'v')})).sort((a,b)=>b.v-a.v);
    const top3disc = disciplinas.slice(0,3);
    // Drivers de custo (densidades)
    const dupKm = sumBy(od.duplicacoes||[],'km')+sumBy(od.contornos||[],'km');
    const dupPct = ext?dupKm/ext*100:0;
    const oaeN = sumBy(od.oaes||[],'n');
    const oaeDens = ext?oaeN/ext:0;
    const interN = sumBy(od.diamantes||[],'n')+sumBy(od.trombetas||[],'n')+sumBy(od.parclos||[],'n')+sumBy(od.rotatorias||[],'n');
    // Contexto urbano
    const cicKm = sumBy(od.ciclovias||[],'km');
    const margKm = sumBy(od.marginais||[],'km');
    const passN = sumBy(od.passarelas||[],'n');
    const ctxUrbano = cicKm>5 || margKm>5 || passN>10;
    // Score de complexidade
    const complexidade = dupPct + oaeDens*100 + (cicKm>0?10:0) + (margKm>0?5:0) + interN/ext*10*5;
    const lvlComplex = complexidade>50?'ALTA':(complexidade>30?'MÉDIA':'BAIXA');

    return {
      sub:'Conclusão',
      status:'ok',
      score_qualidade: comp.inconsistencias.length===0 ? 100 : Math.max(0, 100 - comp.inconsistencias.length*5),
      executive: `O Lote ${sig} (${lote.nome}) apresenta complexidade ${lvlComplex} com custo SICRO de R$ ${(totSicro/1e6).toFixed(0)} milhões em ${ext.toFixed(1)} km, equivalente a R$ ${(rkm/1e3).toFixed(0)}/km. ${desvio_pct>0?'Está '+desvio_pct.toFixed(1)+'% acima':'Está '+Math.abs(desvio_pct).toFixed(1)+'% abaixo'} da média paramétrica dos 3 lotes PR (R$ 1.340/km). A relação SICRO/CAPEX é de ${(ratio*100).toFixed(1)}%, dentro do range esperado para concessões ANTT.`,
      quantitativa:{
        capex_total_bi: lote.capex_total_b,
        sicro_total_mi: (totSicro/1e6).toFixed(0),
        rkm_lote: (rkm/1e3).toFixed(0),
        rkm_benchmark_pr: '1.340',
        desvio_vs_benchmark_pct: desvio_pct.toFixed(1),
        sicro_capex_ratio: (ratio*100).toFixed(2)+'%',
        n_rodovias: (od.sistema||[]).length,
        n_itens_sicro: Object.values(cf).reduce((s,a)=>s+a.length,0),
        top3_disciplinas: top3disc.map(d=>({disc:d.d,r$mi:(d.v/1e6).toFixed(0),pct:(d.v/totSicro*100).toFixed(1)+'%'})),
      },
      qualitativa:{
        complexidade_lvl: lvlComplex,
        contexto: ctxUrbano ? 'Periurbano/Urbano (ciclovias='+cicKm.toFixed(1)+'km · marginais='+margKm.toFixed(1)+'km · passarelas='+passN+')' : 'Predominantemente rural',
        drivers_principais: [
          dupPct>30?`Alta intensidade de duplicação (${dupPct.toFixed(1)}% do trecho)`:null,
          oaeDens>=0.3?`Alta densidade de OAEs (${oaeDens.toFixed(2)}/km · ${oaeN} unidades)`:null,
          interN/ext*10>2?`Conurbação significativa (${interN} interseções · ${(interN/ext*10).toFixed(2)}/10km)`:null,
          ctxUrbano?'Travessias urbanas relevantes':null,
        ].filter(Boolean),
        observacoes: top3disc[0] ? `${top3disc[0].d} é a disciplina dominante (${(top3disc[0].v/totSicro*100).toFixed(1)}% do SICRO).` : '',
      },
      recomendacoes: [
        comp.inconsistencias.length>0 ? '⚠ Resolver as '+comp.inconsistencias.length+' inconsistências PER × MEF identificadas' : null,
        desvio_pct>15?'⚠ R$/km acima do benchmark — investigar drivers críticos':null,
        desvio_pct<-15?'⚠ R$/km abaixo do benchmark — possível sub-orçamentação':null,
        '✓ Validar BDIs (atual: 23.20-23.39%) contra MEF Vol II',
        '✓ Cruzar SICRO consolidado com base oficial DNIT-PR atualizada',
      ].filter(Boolean),
    };
  }

  async function rodarOrquestrador(){
    setEstado('rodando');
    setProgresso([]);
    setRelatorio(null);
    setErroApi(null);
    const ctx = {lote, sig, cf, od, ext};
    const out = await executarPipeline('analiseLote', ctx, (ev) => {
      setProgresso(prev => {
        const idx = prev.findIndex(x => x.agenteId === ev.agenteId);
        const a = AGENTES_CATALOG[ev.agenteId];
        const item = {
          step: prev.length + 1,
          agenteId: ev.agenteId,
          nome: a?.nome || ev.agenteId,
          status: ev.status,
          msg: ev.output?.msg || ev.erro,
        };
        if (idx >= 0) { const novo=[...prev]; novo[idx]={...novo[idx], status:ev.status, msg:item.msg}; return novo; }
        return [...prev, item];
      });
    });
    setRelatorio({
      comp: out.comparador || {},
      conc: out.conclusao || {},
      narrativaAI: out.narrativaAI?.narrativa || null,
      gerado_em: new Date().toLocaleString('pt-BR'),
    });
    if (out.narrativaAI?.erro) setErroApi(out.narrativaAI.erro);
    setEstado('concluido');
  }

  function exportarPDF(){
    const w = window.open('','_blank','width=900,height=700');
    if(!w) return alert('Bloqueador de popup ativo. Permita popups para exportar.');
    const r = relatorio;
    const html = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="utf-8"><title>Relatório ${lote.nome}</title>
<style>
body{font-family:'Segoe UI',sans-serif;color:#1f2937;max-width:760px;margin:30px auto;padding:0 24px;line-height:1.55;font-size:11pt}
h1{color:#1e3a8a;border-bottom:2px solid #1d4ed8;padding-bottom:6px}
h2{color:#475569;margin-top:24px;font-size:13pt}
h3{color:#1d4ed8;font-size:11pt;margin-top:14px}
table{width:100%;border-collapse:collapse;font-size:10pt;margin:8px 0}
th,td{padding:5px 8px;border:1px solid #e5e7eb;text-align:left}
th{background:#f3f4f6;font-weight:700}
.kpi{display:inline-block;padding:6px 10px;background:#dbeafe;color:#1e40af;border-radius:4px;font-size:10pt;margin-right:6px;margin-bottom:6px}
.alert{padding:10px;background:#fef3c7;border-left:4px solid #d97706;margin:10px 0;font-size:10pt}
.ok{color:#16a34a}.err{color:#ea580c}.warn{color:#d97706}
ul{padding-left:18px}li{margin:3px 0}
@media print{@page{margin:1.6cm}body{margin:0;max-width:none}}
</style></head><body>
<h1>Relatório Executivo · ${lote.nome}</h1>
<p style="color:#64748b;font-size:9.5pt;margin-top:-4px">Gerado pelo Agente Orquestrador · ${r.gerado_em} · MANTA Associados</p>

<h2>1. Resumo Executivo</h2>
<p>${r.conc.executive}</p>
${r.narrativaAI?`<div class="alert"><b>Narrativa AI (Claude Sonnet):</b><br>${r.narrativaAI}</div>`:''}

<h2>2. Análise Quantitativa</h2>
<div>
  <span class="kpi">CAPEX: R$ ${r.conc.quantitativa.capex_total_bi} bi</span>
  <span class="kpi">SICRO: R$ ${r.conc.quantitativa.sicro_total_mi} mi</span>
  <span class="kpi">R$/km: R$ ${r.conc.quantitativa.rkm_lote}/km</span>
  <span class="kpi">Desvio benchmark: ${r.conc.quantitativa.desvio_vs_benchmark_pct}%</span>
  <span class="kpi">SICRO/CAPEX: ${r.conc.quantitativa.sicro_capex_ratio}</span>
  <span class="kpi">${r.conc.quantitativa.n_rodovias} rodovias</span>
  <span class="kpi">${r.conc.quantitativa.n_itens_sicro} itens SICRO</span>
</div>
<h3>Top 3 Disciplinas (R$ SICRO)</h3>
<table><tr><th>Disciplina</th><th>R$ (mi)</th><th>%</th></tr>
${r.conc.quantitativa.top3_disciplinas.map(d=>`<tr><td>${d.disc}</td><td>R$ ${d.r$mi}</td><td>${d.pct}</td></tr>`).join('')}
</table>

<h2>3. Análise Qualitativa</h2>
<p><b>Complexidade:</b> ${r.conc.qualitativa.complexidade_lvl}</p>
<p><b>Contexto:</b> ${r.conc.qualitativa.contexto}</p>
<h3>Drivers principais</h3>
<ul>${r.conc.qualitativa.drivers_principais.map(d=>`<li>${d}</li>`).join('') || '<li>Sem drivers críticos identificados</li>'}</ul>
<p>${r.conc.qualitativa.observacoes}</p>

<h2>4. Comparador PER × MEF</h2>
<p>Status: <b class="${r.comp.status==='ok'?'ok':(r.comp.status==='atencao'?'warn':'err')}">${r.comp.status.toUpperCase()}</b> · ${r.comp.msg}</p>
<p style="font-size:9.5pt;color:#64748b">${r.comp.cobertura||''}</p>
${r.comp.inconsistencias.length>0?`<table><tr><th>Severidade</th><th>Campo</th><th>v13</th><th>PER</th><th>Diff</th></tr>
${r.comp.inconsistencias.map(i=>`<tr><td>${i.severidade}</td><td>${i.campo}</td><td>${i.valor_v9}</td><td>${i.valor_per}</td><td>${i.diff}</td></tr>`).join('')}
</table>`:'<p class="ok">✓ Nenhuma inconsistência encontrada</p>'}

<h2>5. Recomendações</h2>
<ul>${r.conc.recomendacoes.map(r=>`<li>${r}</li>`).join('')}</ul>

<h2>6. Score de Qualidade</h2>
<p style="font-size:24pt;font-weight:700;color:${r.conc.score_qualidade>=85?'#16a34a':(r.conc.score_qualidade>=60?'#d97706':'#ea580c')};margin:10px 0">${r.conc.score_qualidade}/100</p>

<hr style="margin-top:30px;border:none;border-top:1px solid #e5e7eb">
<p style="font-size:8.5pt;color:#94a3b8;text-align:center;margin-top:14px">Portal Manta · v12 · Lotes PR ANTT · gerado pelo Agente Orquestrador</p>
<script>window.onload=function(){setTimeout(window.print,300)}</script>
</body></html>`;
    w.document.write(html);
    w.document.close();
  }

  return(<div style={S.page}>
    <div style={S.h1}>🧠 Agente Orquestrador · {lote.nome}</div>
    <div style={S.sub}>
      Executa Comparador PER×MEF + Conclusão · gera relatório executivo descritivo · qualitativo e quantitativo · exporta PDF
    </div>

    {/* Botão principal */}
    <div style={{...S.cardP,marginBottom:14,display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
      <button onClick={rodarOrquestrador} disabled={estado==='rodando'} style={{
        padding:'10px 20px',fontSize:12,fontWeight:700,
        background:estado==='rodando'?V.g0:V.bl,color:estado==='rodando'?V.gr:'#fff',
        border:'none',borderRadius:5,cursor:estado==='rodando'?'wait':'pointer'
      }}>{estado==='rodando'?'⏳ Executando…':'▶ Executar análise completa'}</button>
      {relatorio && <button onClick={exportarPDF} style={{
        padding:'10px 20px',fontSize:12,fontWeight:700,
        background:'#fff',color:V.bl,border:'2px solid '+V.bl,borderRadius:5,cursor:'pointer'
      }}>📄 Exportar PDF</button>}
      {progresso.length>0 && <span style={{fontSize:10,color:V.gr}}>{progresso.length} passo{progresso.length>1?'s':''} · {estado}</span>}
    </div>

    {/* Progresso */}
    {progresso.length>0 && <div style={{...S.card,padding:'10px 14px',marginBottom:14}}>
      {progresso.map(p=>(<div key={p.step} style={{display:'flex',alignItems:'center',gap:10,padding:'5px 0',fontSize:11}}>
        <span style={{
          width:24,height:24,borderRadius:'50%',background:p.status==='ok'?V.gn:(p.status==='rodando'?V.bl:p.status==='atencao'?V.am||'#d97706':p.status==='pendente'?V.gr:V.or),
          color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700
        }}>{p.status==='ok'?'✓':(p.status==='rodando'?'⏳':p.status==='atencao'?'!':p.status==='pendente'?'·':'✕')}</span>
        <span style={{fontWeight:700,color:V.br}}>{p.nome}</span>
        {p.msg && <span style={{color:V.gr}}>· {p.msg}</span>}
      </div>))}
    </div>}

    {/* Relatório consolidado */}
    {relatorio && <>
      <div style={{...S.cardP,marginBottom:14,borderLeft:'4px solid '+V.bl}}>
        <div style={{fontSize:11.5,fontWeight:700,color:V.br,marginBottom:6}}>1 · Resumo Executivo</div>
        <div style={{fontSize:11,color:V.br,lineHeight:1.65}}>{relatorio.conc.executive}</div>
        {relatorio.narrativaAI && <div style={{...S.cardP,marginTop:10,background:'#eff6ff',borderLeft:'4px solid '+V.bl}}>
          <div style={{fontSize:10,fontWeight:700,color:V.bl,letterSpacing:.4,marginBottom:4}}>NARRATIVA AI · CLAUDE SONNET</div>
          <div style={{fontSize:11,color:V.br,lineHeight:1.6}}>{relatorio.narrativaAI}</div>
        </div>}
      </div>

      {/* KPIs */}
      <div style={{...S.cardP,marginBottom:14}}>
        <div style={{fontSize:11.5,fontWeight:700,color:V.br,marginBottom:8}}>2 · Análise Quantitativa</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:8}}>
          {[
            ['CAPEX','R$ '+relatorio.conc.quantitativa.capex_total_bi+' bi'],
            ['SICRO','R$ '+relatorio.conc.quantitativa.sicro_total_mi+' mi'],
            ['R$/km','R$ '+relatorio.conc.quantitativa.rkm_lote+'/km'],
            ['Desvio benchmark',relatorio.conc.quantitativa.desvio_vs_benchmark_pct+'%'],
            ['SICRO/CAPEX',relatorio.conc.quantitativa.sicro_capex_ratio],
            ['Rodovias',relatorio.conc.quantitativa.n_rodovias],
            ['Itens SICRO',relatorio.conc.quantitativa.n_itens_sicro],
          ].map(([l,v])=>(<div key={l} style={{padding:'6px 10px',background:V.g0,borderRadius:4,borderLeft:'3px solid '+V.bl}}>
            <div style={{fontSize:9,fontWeight:700,color:V.gr,letterSpacing:.3}}>{l}</div>
            <div style={{fontSize:12,fontWeight:700,color:V.br}}>{v}</div>
          </div>))}
        </div>
        <div style={{marginTop:10,fontSize:10.5,color:V.br}}>
          <b>Top 3 disciplinas:</b> {relatorio.conc.quantitativa.top3_disciplinas.map(d=>`${d.disc} (R$${d.r$mi}mi · ${d.pct})`).join(' · ')}
        </div>
      </div>

      {/* Qualitativa */}
      <div style={{...S.cardP,marginBottom:14}}>
        <div style={{fontSize:11.5,fontWeight:700,color:V.br,marginBottom:8}}>3 · Análise Qualitativa</div>
        <div style={{fontSize:11,color:V.br,lineHeight:1.6}}>
          <b>Complexidade:</b> <span style={{...S.pillBase,background:relatorio.conc.qualitativa.complexidade_lvl==='ALTA'?V.or:(relatorio.conc.qualitativa.complexidade_lvl==='MÉDIA'?V.am||'#d97706':V.gn),color:'#fff',padding:'2px 8px'}}>{relatorio.conc.qualitativa.complexidade_lvl}</span>
          <br/><b>Contexto:</b> {relatorio.conc.qualitativa.contexto}
          <br/><b>Drivers principais:</b>
          <ul style={{paddingLeft:20,margin:'4px 0'}}>
            {relatorio.conc.qualitativa.drivers_principais.map((d,i)=>(<li key={i}>{d}</li>))}
            {relatorio.conc.qualitativa.drivers_principais.length===0 && <li>Sem drivers críticos identificados</li>}
          </ul>
          <p style={{margin:'4px 0'}}>{relatorio.conc.qualitativa.observacoes}</p>
        </div>
      </div>

      {/* Comparador */}
      <div style={{...S.cardP,marginBottom:14,borderLeft:'4px solid '+(relatorio.comp.status==='ok'?V.gn:V.or)}}>
        <div style={{fontSize:11.5,fontWeight:700,color:V.br,marginBottom:6}}>4 · Comparador PER × MEF</div>
        <div style={{fontSize:11,color:V.br}}>
          Status: <b style={{color:relatorio.comp.status==='ok'?V.gn:V.or}}>{relatorio.comp.status.toUpperCase()}</b> · {relatorio.comp.msg}
          {relatorio.comp.cobertura && <span style={{color:V.gr,marginLeft:6,fontSize:10}}>· {relatorio.comp.cobertura}</span>}
        </div>
        {relatorio.comp.inconsistencias.length>0 && <table style={{width:'100%',borderCollapse:'collapse',fontSize:10.5,marginTop:8}}>
          <thead><tr style={{borderBottom:'2px solid '+V.b1,background:V.g0}}>
            <th style={{padding:'5px 8px',textAlign:'left'}}>Severidade</th>
            <th style={{padding:'5px 8px',textAlign:'left'}}>Campo</th>
            <th style={{padding:'5px 8px',textAlign:'right'}}>v13</th>
            <th style={{padding:'5px 8px',textAlign:'right'}}>PER</th>
            <th style={{padding:'5px 8px',textAlign:'right'}}>Diff</th>
          </tr></thead>
          <tbody>
            {relatorio.comp.inconsistencias.map((i,k)=>(<tr key={k} style={{borderBottom:'1px solid '+V.g0}}>
              <td style={{padding:'5px 8px'}}><span style={{...S.pillBase,background:i.severidade==='alta'?V.or:i.severidade==='media'?V.am||'#d97706':V.gn,color:'#fff',padding:'1px 6px',fontSize:9}}>{i.severidade.toUpperCase()}</span></td>
              <td style={{padding:'5px 8px'}}>{i.campo}</td>
              <td style={{padding:'5px 8px',textAlign:'right',fontFamily:'monospace'}}>{i.valor_v9}</td>
              <td style={{padding:'5px 8px',textAlign:'right',fontFamily:'monospace'}}>{i.valor_per}</td>
              <td style={{padding:'5px 8px',textAlign:'right',fontFamily:'monospace',fontWeight:700}}>{i.diff}</td>
            </tr>))}
          </tbody>
        </table>}
      </div>

      {/* Recomendações */}
      <div style={{...S.cardP,marginBottom:14}}>
        <div style={{fontSize:11.5,fontWeight:700,color:V.br,marginBottom:6}}>5 · Recomendações</div>
        <ul style={{paddingLeft:20,fontSize:11,color:V.br,lineHeight:1.6}}>
          {relatorio.conc.recomendacoes.map((r,i)=>(<li key={i}>{r}</li>))}
        </ul>
      </div>

      {/* Score */}
      <div style={{...S.cardP,textAlign:'center'}}>
        <div style={{fontSize:11,fontWeight:700,color:V.gr,letterSpacing:.4}}>SCORE DE QUALIDADE</div>
        <div style={{fontSize:36,fontWeight:700,color:relatorio.conc.score_qualidade>=85?V.gn:(relatorio.conc.score_qualidade>=60?V.am||'#d97706':V.or),marginTop:4}}>
          {relatorio.conc.score_qualidade}<span style={{fontSize:14,color:V.gr}}>/100</span>
        </div>
        <div style={{fontSize:10,color:V.gr,marginTop:4}}>{relatorio.gerado_em}</div>
      </div>
    </>}

    {!relatorio && estado==='idle' && <div style={{...S.cardP,fontSize:11,color:V.gr,textAlign:'center',padding:'20px 14px',fontStyle:'italic'}}>
      Clique em "▶ Executar análise completa" para rodar o orquestrador. Em modo offline, usa template determinístico. Com backend ON, enriquece com narrativa Claude Sonnet.
    </div>}
  </div>);
});

export { AgenteIAPage };
export default AgenteIAPage;
